import { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowLeft, LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

interface PageHeaderProps {
  /** Optional page title */
  title?: string
  /** Optional title prefix */
  titlePrefix?: string
  /** Optional description text below the title */
  description?: string | ReactNode
  /** Optional action button (usually "New X") */
  action?: {
    label: string
    icon?: LucideIcon
    href?: string
  }
  /** Optional back button */
  backButton?: {
    label: string
    onClick: () => void
  }
  /** Custom actions to render instead of the default action button */
  customActions?: ReactNode
  /** Optional prefix element (e.g., avatar) to show before the title */
  prefix?: ReactNode
  /** Optional metadata to show below the title */
  metadata?: ReactNode
}

export function PageHeader({
  title,
  titlePrefix,
  description,
  action,
  backButton,
  customActions,
  prefix,
  metadata,
}: PageHeaderProps) {
  const ActionIcon = action?.icon

  return (
    <header className="space-y-4 mt-3">
      {backButton && (
        <Button variant="ghost" size="sm" onClick={backButton.onClick} className="-ml-2">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {backButton.label}
        </Button>
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {prefix && <div className="flex-shrink-0">{prefix}</div>}
          <div className="space-y-6 flex-1 min-w-0">
            <header className="space-y-2">
              {title && (
                <h1 className="text-3xl sm:text-4xl font-bold">
                  {titlePrefix && (
                    <span className="text-muted-foreground font-normal">{titlePrefix}</span>
                  )}
                  {` `}
                  {title}
                </h1>
              )}
              {description && <p className="text-muted-foreground max-w-[55ch]">{description}</p>}
            </header>
            {metadata && <div className="flex flex-wrap items-center gap-3">{metadata}</div>}
          </div>
        </div>

        {customActions ||
          (action && (
            <Button asChild className="flex-shrink-0 w-full sm:w-auto">
              <Link to={action.href!}>
                {ActionIcon && <ActionIcon className="mr-2 h-4 w-4" />}
                {action.label}
              </Link>
            </Button>
          ))}
      </div>
    </header>
  )
}
